import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message
} from "antd";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PageHeader from "../shared/PageHeader";
import MoneyInput from "../shared/MoneyInput";
import { errorText, openBlob } from "../api";
import { formatDate, formatPeriod } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { RunSteps, STEP_LABELS } from "./RunsList";
import { FilingRow } from "./PayrollFilings";
import {
  adjustSlip,
  approveRun,
  calculateRun,
  createSepa,
  deleteRun,
  fileRun,
  getRun,
  listBankAccounts,
  markPaid,
  sepaXmlBlob
} from "./api";

const RunDetail = () => {
  const { t } = useTranslation("accounting");
  const { id } = useParams();
  const history = useHistory();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [sepaOpen, setSepaOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filed, setFiled] = useState(null);
  const [editForm] = Form.useForm();
  const [sepaForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRun(id);
      setRun(res.data.run);
    } catch (err) {
      message.error(errorText(err, t("Could not load the payroll run")));
    }
    setLoading(false);
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (key, fn, success) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res?.data?.run) setRun(res.data.run);
      if (success) message.success(success);
      return res;
    } catch (err) {
      const missing = err?.response?.data?.missing;
      message.error(missing ? `${t("IBAN missing")}: ${missing.join(", ")}` : errorText(err));
      return null;
    } finally {
      setBusy("");
    }
  };

  const openEdit = slip => {
    setEditing(slip);
    editForm.setFieldsValue({
      hours: slip.adjustment?.hours ?? slip.hours,
      einmalzahlung: slip.adjustment?.einmalzahlung || 0,
      note: slip.adjustment?.note || ""
    });
  };

  const saveEdit = async () => {
    const values = await editForm.validateFields();
    const body = { einmalzahlung: values.einmalzahlung || 0, note: values.note || "" };
    if (editing.employee.workSchedule !== "monthly") body.hours = values.hours;
    const res = await act("edit", () => adjustSlip(id, editing._id, body), t("Slip recalculated"));
    if (res) setEditing(null);
  };

  const openSepa = async () => {
    try {
      const res = await listBankAccounts();
      setAccounts(res.data.accounts || []);
      sepaForm.setFieldsValue({
        bankAccountId: res.data.accounts?.[0]?._id,
        executionDate: dayjs().add(1, "day")
      });
      setSepaOpen(true);
    } catch (err) {
      message.error(errorText(err));
    }
  };

  const downloadSepa = () =>
    openBlob(sepaXmlBlob(id), `Lohn_${run.period}_pain001.xml`).catch(err =>
      message.error(errorText(err))
    );

  const submitSepa = async () => {
    const values = await sepaForm.validateFields();
    const res = await act(
      "sepa",
      () =>
        createSepa(id, {
          bankAccountId: values.bankAccountId,
          executionDate: values.executionDate.format("YYYY-MM-DD")
        }),
      t("SEPA file created")
    );
    if (res) {
      setSepaOpen(false);
      downloadSepa();
    }
  };

  const file = async () => {
    const res = await act("file", () => fileRun(id), t("Filings created"));
    if (res) setFiled(res.data);
  };

  if (!run) return <Spin spinning={loading} />;
  const status = run.status;
  const editable = status === "calculated";
  const journalEntry = run.journalEntries?.[0];
  const filings = [
    run.filings?.lohnsteuerAnmeldung,
    ...(run.filings?.beitragsnachweise || []),
    ...(run.filings?.deuev || []),
    run.filings?.soka
  ].filter(f => f && f._id);

  const money = key => ({ dataIndex: key, align: "end", render: v => <Amount cents={v} /> });
  const columns = [
    {
      title: t("Employee"),
      dataIndex: ["employee", "name"],
      render: (v, s) => (
        <Space direction="vertical" size={0}>
          <span>
            {v} {s.locked ? <Tag>{t("locked")}</Tag> : null}{" "}
            {s.minijob ? <Tag color="purple">{t("Minijob")}</Tag> : null}{" "}
            {s.midijob ? <Tag color="blue">{t("Midijob")}</Tag> : null}
          </span>
          {s.warnings.length ? (
            <span>
              {s.warnings.map(w => (
                <Tag color="red" key={w}>
                  {w}
                </Tag>
              ))}
            </span>
          ) : null}
        </Space>
      )
    },
    {
      title: t("Hours"),
      dataIndex: "hours",
      align: "end",
      render: (v, s) =>
        s.employee.workSchedule === "monthly" ? (
          "—"
        ) : (
          <span className="acct-num">{v.toFixed(2)}</span>
        )
    },
    { title: t("Gross"), ...money("gross") },
    { title: t("Tax"), ...money("wageTax") },
    { title: t("SV employee"), ...money("employeeSv") },
    { title: t("Net"), ...money("net") },
    { title: t("Payout"), ...money("payout") },
    { title: t("SV employer"), ...money("employerSv") },
    { title: t("Employer cost"), ...money("employerCost") },
    {
      title: "",
      key: "actions",
      render: (_, s) =>
        editable && !s.locked ? (
          <Button size="small" onClick={() => openEdit(s)}>
            {t("Edit")}
          </Button>
        ) : null
    }
  ];

  return (
    <Spin spinning={loading || Boolean(busy)}>
      <PageHeader
        title={`${t("Lohnlauf")} ${formatPeriod(run.period)}`}
        subtitle={<Tag>{t(STEP_LABELS[status])}</Tag>}
        extra={
          <>
            <Button onClick={() => history.push("/user/accounting/payroll")}>{t("Back")}</Button>
            {["open", "calculated"].includes(status) ? (
              <Button
                type="primary"
                onClick={() => act("calc", () => calculateRun(id), t("Run calculated"))}
              >
                {t("Calculate")}
              </Button>
            ) : null}
            {status === "calculated" ? (
              <Popconfirm
                title={t(
                  "Approve the run? All slips are locked and the payroll is booked in the journal."
                )}
                onConfirm={() => act("approve", () => approveRun(id), t("Run approved and booked"))}
              >
                <Button type="primary">{t("Approve")}</Button>
              </Popconfirm>
            ) : null}
            {["approved", "paid", "filed"].includes(status) && !run.sepaBatch ? (
              <Button onClick={openSepa}>{t("Create SEPA file")}</Button>
            ) : null}
            {run.sepaBatch ? (
              <Button onClick={downloadSepa}>{t("Download SEPA XML")}</Button>
            ) : null}
            {["approved", "filed"].includes(status) && !run.paidAt ? (
              <Popconfirm
                title={t("Mark this run as paid?")}
                onConfirm={() => act("paid", () => markPaid(id), t("Marked as paid"))}
              >
                <Button>{t("Mark as paid")}</Button>
              </Popconfirm>
            ) : null}
            {["approved", "paid", "filed"].includes(status) ? (
              <Button onClick={file}>{t("Create filings")}</Button>
            ) : null}
            {["open", "calculated"].includes(status) ? (
              <Popconfirm
                title={t("Delete this payroll run? Locked slips are kept.")}
                onConfirm={() =>
                  act("del", async () => {
                    await deleteRun(id);
                    history.push("/user/accounting/payroll");
                  })
                }
              >
                <Button danger>{t("Delete")}</Button>
              </Popconfirm>
            ) : null}
          </>
        }
      />
      <Card style={{ marginBottom: 16 }}>
        <RunSteps status={status} size="default" />
      </Card>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" className="acct-kpi">
            <Statistic title={t("Employees")} value={run.totals?.employeeCount || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Gross")}
              valueRender={() => <Amount cents={run.totals?.gross} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Payout")}
              valueRender={() => <Amount cents={run.totals?.payout} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" className="acct-kpi">
            <Statistic
              title={t("Employer cost")}
              valueRender={() => <Amount cents={run.totals?.employerCost} strong />}
            />
          </Card>
        </Col>
      </Row>
      {run.warnings?.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("Warnings")}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {run.warnings.map(w => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card title={t("Employees")} size="small">
            <ResponsiveTable
              mobileLayout="table"
              className="acct-grid"
              rowKey="_id"
              size="small"
              pagination={false}
              dataSource={run.slips || []}
              columns={columns}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>{t("Total")}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  {[
                    "gross",
                    "wageTax",
                    "employeeContributions",
                    "net",
                    "payout",
                    "employerContributions",
                    "employerCost"
                  ].map((key, i) => (
                    <Table.Summary.Cell index={i + 2} key={key} align="end">
                      <Amount cents={run.totals?.[key]} strong />
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={9} />
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title={t("Totals")} size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t("Wages (hourly)")}>
                <Amount cents={run.totals?.wages} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Salaries (monthly)")}>
                <Amount cents={run.totals?.salaries} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Minijob wages")}>
                <Amount cents={run.totals?.minijobWages} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Minijob flat tax 2 %")}>
                <Amount cents={run.totals?.minijobFlatTax} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Lohnsteuer, Soli, KiSt")}>
                <Amount cents={run.totals?.wageTax} />
              </Descriptions.Item>
              <Descriptions.Item label={t("SV employer")}>
                <Amount cents={run.totals?.employerContributions} />
              </Descriptions.Item>
              <Descriptions.Item label={t("SOKA-BAU Umlage")}>
                <Amount cents={run.totals?.sokaBau} />
              </Descriptions.Item>
              <Descriptions.Item label={t("VWL")}>
                <Amount cents={run.totals?.vwl} />
              </Descriptions.Item>
              <Descriptions.Item label={t("Employer cost")}>
                <Amount cents={run.totals?.employerCost} strong />
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card
            title={t("Contributions per Krankenkasse")}
            size="small"
            style={{ marginBottom: 16 }}
          >
            <List
              size="small"
              dataSource={run.totals?.byKrankenkasse || []}
              renderItem={k => (
                <List.Item>
                  <List.Item.Meta
                    title={<span className="acct-wrap">{k.name}</span>}
                    description={`${k.betriebsnummer || "—"} · ${k.employeeCount} ${t("employees")}`}
                  />
                  <Amount cents={k.total} strong />
                </List.Item>
              )}
            />
          </Card>
          {journalEntry ? (
            <Card title={t("Journal entry")} size="small" style={{ marginBottom: 16 }}>
              <Typography.Text className="acct-wrap">
                {journalEntry.entryNo}/{journalEntry.year} · {formatDate(journalEntry.date)} ·{" "}
                {journalEntry.text}
              </Typography.Text>
            </Card>
          ) : null}
          {run.sepaBatch ? (
            <Card title={t("SEPA batch")} size="small" style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t("Execution date")}>
                  {formatDate(run.sepaBatch.executionDate)}
                </Descriptions.Item>
                <Descriptions.Item label={t("Transfers")}>
                  {run.sepaBatch.items?.length}
                </Descriptions.Item>
                <Descriptions.Item label={t("Total")}>
                  <Amount cents={run.sepaBatch.totalAmount} />
                </Descriptions.Item>
                <Descriptions.Item label={t("Status")}>{t(run.sepaBatch.status)}</Descriptions.Item>
              </Descriptions>
            </Card>
          ) : null}
        </Col>
      </Row>
      {filings.length || filed ? (
        <Card title={t("Filings of this run")} size="small" style={{ marginTop: 16 }}>
          {filed?.notes?.length ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={filed.notes.join(" ")}
            />
          ) : null}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t(
              "Lohnsteuer-Anmeldung and Lohnsteuerbescheinigung are transmitted through ELSTER in the tax module. Beitragsnachweis, DEÜV and SOKA-BAU payloads are downloaded here and uploaded at the SV-Meldeportal / SOKA-BAU portal."
            )}
          />
          <List size="small" dataSource={filings} renderItem={f => <FilingRow filing={f} />} />
        </Card>
      ) : null}

      <Modal
        rootClassName="acct-module"
        open={Boolean(editing)}
        title={editing ? `${t("Edit slip")} – ${editing.employee.name}` : ""}
        onOk={saveEdit}
        onCancel={() => setEditing(null)}
        okText={t("Recalculate")}
        rootClassName="acct-module"
      >
        <Form form={editForm} layout="vertical">
          {editing?.employee.workSchedule !== "monthly" ? (
            <Form.Item
              name="hours"
              label={t("Hours worked")}
              extra={t("Overrides the hours from the shift plan for this month.")}
            >
              <InputNumber min={0} step={0.25} style={{ width: "100%" }} />
            </Form.Item>
          ) : null}
          <Form.Item name="einmalzahlung" label={t("One-off payment (sonstiger Bezug)")}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="note" label={t("Note")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        rootClassName="acct-module"
        open={sepaOpen}
        title={t("Create SEPA file")}
        onOk={submitSepa}
        onCancel={() => setSepaOpen(false)}
        okText={t("Create and download")}
        rootClassName="acct-module"
      >
        <p>
          {t(
            "One transfer per employee (Auszahlungsbetrag), per Krankenkasse (Beitragsnachweis), to the Finanzamt (Lohnsteuer) and to the Minijob-Zentrale."
          )}
        </p>
        <Form form={sepaForm} layout="vertical">
          <Form.Item name="bankAccountId" label={t("Bank account")} rules={[{ required: true }]}>
            <Select
              options={accounts.map(a => ({ value: a._id, label: `${a.name} · ${a.iban}` }))}
              notFoundContent={t("No bank account. Add one in the bank module first.")}
            />
          </Form.Item>
          <Form.Item name="executionDate" label={t("Execution date")} rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default RunDetail;
